import React, { useState } from 'react';
import { Field, Form, Formik } from 'formik';
import { FormattedMessage } from 'react-intl';
import FormTextField from '../../common/form/FormTextField';
import CommonButton from '../../common/CommonButton';
import { fieldCustomStyles } from '../administration/AddNewItemDialog';
import { BLACK_COLOR, BORDER_DARK_TEXT, WHITE_COLOR } from '../../../const/color-const';
import successAnimation from '../../../style/animations/success.json';
import { useValidator } from '../../../utils/validators';
import { connect } from 'react-redux';
import emailjs from '@emailjs/browser';
import { Box } from '@mui/material';
import PropTypes from 'prop-types';
import Lottie from 'lottie-react';
import { compose } from 'redux';
import { withStyles } from '@material-ui/core';
import { formatMachinesListAsHTMLDiv } from '../contact/Utils';

const styles = theme => ({
  contactWrapper: {
    padding: theme.spacing(0, 1, 2, 1),
    width: '100%',
    '& h2': {
      margin: 0,
    },
  },
  form: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  formContent: {
    width: '100%',
    display: 'flex',
    flexWrap: 'wrap',
    textAlign: 'center',
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
    marginBottom: theme.spacing(6),
    '& [class*="MuiTextField"]': {
      ...fieldCustomStyles(theme),
      margin: theme.spacing(1),
      boxShadow: 'none',
    },
    '& button': {
      padding: theme.spacing(0, 1, 0, 1),
      width: 120,
      height: 56,
      '& span': {
        width: '100%',
      },
    },
    '& div': {
      margin: 0,
      height: 60,
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      [theme.breakpoints.up('sm')]: {
        margin: theme.spacing(0, 1, 0, 1),
      },
      '&:before, &:after': {
        display: 'none',
      },
      '& label': {
        top: -5,
        zIndex: 1,
        color: 'gray',
        marginLeft: 10,
        position: 'absolute',
      },
      '& label[class*="MuiFormLabel-filled"], & label[class*="Mui-focused"]': {
        top: 2,
      },
      '& input': {
        border: theme.palette.type === 'light' ? '1px solid Silver' : `1px solid ${BORDER_DARK_TEXT}`,
        background: theme.palette.type === 'light' ? WHITE_COLOR : BLACK_COLOR,
        padding: theme.spacing(0, 0, 0, 1),
        borderRadius: 4,
        height: '100%',
      },
      '& p.Mui-error': {
        margin: theme.spacing(0, 0, 0, 1),
        position: 'absolute',
        bottom: -20,
        left: 0,
      },
    },
  },
});

const validator = ({ values, intl: { formatMessage } }) => {
  const errors = {};
  
  if (!values.firstName) {
    errors.firstName = formatMessage({
      id: 'form.firstName.error.required',
      defaultMessage: 'First name is required',
    });
  }
  if (!values.lastName) {
    errors.lastName = formatMessage({
      id: 'form.lastName.error.required',
      defaultMessage: 'First name is required',
    });
  }
  if (!values.email) {
    errors.email = formatMessage({
      id: 'form.email.error.required',
      defaultMessage: 'Email is required',
    });
  } else if (!values.email.includes('@')) {
    errors.email = formatMessage({
      id: 'form.email.error.invalid',
      defaultMessage: 'Invalid email address',
    });
  }
  
  return errors;
};

const UpgradePageForm = ({ classes, machines, user }) => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const formValidator = useValidator(validator);
  
  const SERVICE_ID = process.env.REACT_APP_EMAIL_SERVICE_ID;
  const TEMPLATE_ID = process.env.REACT_APP_EMAIL_TEMPLATE_ID;
  const PUBLIC_KEY = process.env.REACT_APP_EMAIL_PUBLIC_KEY;
  
  const MachinesList = machines?.map(({ CustomerName, Name, Id }) => ({ CustomerName, Name, Id }));
  const { Firstname, Lastname, UserName, Id, EMail } = user ?? {};
  
  const handleSubmit = (values, { setSubmitting, setErrors }) => {
    const emailParams = {
      Email: values.email,
      Firstname: values.firstName,
      Lastname: values?.lastName,
      UserName,
      Id,
      MachinesList: formatMachinesListAsHTMLDiv(MachinesList),
    };
    
    emailjs.send(SERVICE_ID, TEMPLATE_ID, emailParams, PUBLIC_KEY)
      .then(() => setIsSubmitted(true))
      .catch(err => {
        console.error('Failed to send email.', err);
        setErrors({ submit: 'Failed to send message. Please try again later.' });
      })
      .finally(() => setSubmitting(false));
  };
  
  return <div className={classes.contactWrapper}>
    <Formik
      initialValues={{
        email: EMail || '',
        firstName: Firstname || '',
        lastName: Lastname || '',
      }}
      onSubmit={handleSubmit}
      validate={formValidator}>
      {({ isSubmitting, values }) => {
        return (
          <Form className={classes.form}>
            <div className={classes.formContent}>
              {!isSubmitted
                ? formContent(classes, isSubmitting, isSubmitted)
                : successContent()
              }
            </div>
          </Form>
        );
      }}
    </Formik>
  </div>;
};

const successContent = () => (
  <Box justifyContent="center" alignItems="center">
    <h2><FormattedMessage id="message.sent" defaultMessage="Message has been sent" /></h2>
    <Lottie loop={false} animationData={successAnimation} play style={{ width: 60, height: 60 }} />
  </Box>
);

const formContent = (classes, isSubmitting, isSubmitted) => (
  <>
    <Field
      variant="standard"
      name="firstName"
      component={FormTextField}
      label={<FormattedMessage id="page.upgrade.firstName" defaultMessage="First name" />}
      disabled={isSubmitting || isSubmitted}
    />
    <Field
      variant="standard"
      name="lastName"
      component={FormTextField}
      label={<FormattedMessage id="page.upgrade.lastName" defaultMessage="Last name" />}
      disabled={isSubmitting || isSubmitted}
    />
    <Field
      variant="standard"
      name="email"
      component={FormTextField}
      label={<FormattedMessage id="page.upgrade.yourEmail" defaultMessage="Your email" />}
      disabled={isSubmitting || isSubmitted}
    />
    <CommonButton
      size="large"
      type="submit"
      label={<FormattedMessage id="page.upgrade.send" defaultMessage="Send" />}
      disabled={isSubmitting || isSubmitted}
    />
  </>
);

UpgradePageForm.propTypes = {
  classes: PropTypes.object,
  machines: PropTypes.array,
  user: PropTypes.object,
};

export default compose(
  withStyles(styles),
  connect(store => ({
    machines: store.user.machines,
    user: store.user.profile,
  })),
)(UpgradePageForm);